import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getProperties, getProperty } from '@/lib/api';
import { toast } from 'sonner';
import { X, Plus, Bed, Bath, Maximize2, MapPin, Car, Layers, Check, Minus } from 'lucide-react';
import Seo from '@/components/Seo';

const ComparePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState([]);
  const [properties, setProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];
    setSelectedIds(ids);
    loadProperties(ids);
    loadAllProperties();
  }, [searchParams]);

  const loadProperties = async (ids) => {
    if (ids.length === 0) {
      setProperties([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const props = await Promise.all(ids.map(id => getProperty(id).catch(() => null)));
      setProperties(props.filter(Boolean));
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllProperties = async () => {
    try {
      const data = await getProperties({ limit: 50 });
      setAllProperties(data);
    } catch (error) {
      console.error('Error loading all properties:', error);
    }
  };

  const addProperty = (id) => {
    if (selectedIds.length >= 4) {
      toast.error('Maximum 4 properties can be compared');
      return;
    }
    if (selectedIds.includes(id)) return;

    const newIds = [...selectedIds, id];
    setSearchParams({ ids: newIds.join(',') });
    setShowSelector(false);
  };

  const removeProperty = (id) => {
    const newIds = selectedIds.filter(i => i !== id);
    setSearchParams(newIds.length ? { ids: newIds.join(',') } : {});
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} Lac`;
    }
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const defaultImage = "https://images.unsplash.com/photo-1629946488804-217c002178cf?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";

  const getComparisonValue = (value, allValues) => {
    if (!value || allValues.filter(Boolean).length < 2) return 'neutral';
    const numValue = parseFloat(value);
    const numValues = allValues.map(v => parseFloat(v)).filter(Boolean);
    const max = Math.max(...numValues);
    const min = Math.min(...numValues);
    if (numValue === max) return 'best';
    if (numValue === min) return 'worst';
    return 'neutral';
  };

  const CompareRow = ({ label, icon: Icon, values, type = 'text', lowerIsBetter = false }) => {
    const validValues = values.filter(v => v !== null && v !== undefined && v !== '');
    
    return (
      <tr className="border-b">
        <td className="p-4 font-medium text-[#0F172A] bg-[#F9F9F7]">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
            {label}
          </div>
        </td>
        {values.map((value, index) => {
          let displayValue = value || '-';
          let className = 'p-4 text-center';
          
          if (type === 'price' && value) {
            displayValue = formatPrice(value);
          } else if (type === 'area' && value) {
            displayValue = `${value} sqft`;
          }

          if (validValues.length >= 2 && value) {
            const numValue = parseFloat(value);
            const numValues = validValues.map(v => parseFloat(v));
            const max = Math.max(...numValues);
            const min = Math.min(...numValues);
            
            if (lowerIsBetter) {
              if (numValue === min) className += ' bg-green-50 text-green-700 font-medium';
              if (numValue === max) className += ' bg-red-50 text-red-700';
            } else {
              if (numValue === max) className += ' bg-green-50 text-green-700 font-medium';
              if (numValue === min) className += ' bg-red-50 text-red-700';
            }
          }

          return <td key={index} className={className}>{displayValue}</td>;
        })}
        {/* Fill empty slots */}
        {[...Array(4 - values.length)].map((_, i) => (
          <td key={`empty-${i}`} className="p-4 text-center text-muted-foreground">-</td>
        ))}
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <Seo title="Compare Properties Side by Side" description="Compare up to 4 properties side by side on price, area, BHK, amenities, and location." path="/compare" />
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="caption mb-3">Compare</p>
          <h1 className="heading-secondary text-[#0F172A] mb-4">Compare Properties</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Compare up to 4 properties side by side to make an informed decision
          </p>
        </div>

        {/* Property Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {properties.map((prop, index) => (
            <div key={prop.property_id} className="bg-white rounded-sm shadow-sm border border-border/50 overflow-hidden">
              <div className="relative aspect-[4/3]">
                <img
                  src={prop.images?.[0] || defaultImage}
                  alt={prop.title}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeProperty(prop.property_id)}
                  className="absolute top-2 right-2 h-8 w-8 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-50"
                  data-testid={`remove-compare-${prop.property_id}`}
                >
                  <X className="h-4 w-4 text-red-500" />
                </button>
              </div>
              <div className="p-4">
                <Link to={`/properties/${prop.property_id}`} className="font-medium text-[#0F172A] hover:text-[#D4AF37] line-clamp-2">
                  {prop.title}
                </Link>
                <p className="text-sm text-muted-foreground mt-1">{prop.city}</p>
              </div>
            </div>
          ))}

          {/* Add Property Slot */}
          {selectedIds.length < 4 && (
            <div
              onClick={() => setShowSelector(true)}
              className="bg-white rounded-sm border-2 border-dashed border-border hover:border-[#D4AF37] cursor-pointer flex flex-col items-center justify-center min-h-[200px] transition-colors"
              data-testid="add-property-to-compare"
            >
              <Plus className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-muted-foreground">Add Property</span>
            </div>
          )}
        </div>

        {/* Comparison Table */}
        {properties.length >= 2 ? (
          <div className="bg-white rounded-sm shadow-sm border border-border/50 overflow-hidden overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-[#0F172A] text-white">
                  <th className="p-4 text-left font-medium w-48">Feature</th>
                  {properties.map((prop) => (
                    <th key={prop.property_id} className="p-4 text-center font-medium">
                      <span className="line-clamp-1">{prop.title}</span>
                    </th>
                  ))}
                  {[...Array(4 - properties.length)].map((_, i) => (
                    <th key={`empty-header-${i}`} className="p-4"></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <CompareRow 
                  label="Price" 
                  values={properties.map(p => p.price)} 
                  type="price"
                  lowerIsBetter={true}
                />
                <CompareRow 
                  label="Area" 
                  icon={Maximize2}
                  values={properties.map(p => p.area)} 
                  type="area"
                />
                <CompareRow 
                  label="Price per Sqft" 
                  values={properties.map(p => p.area ? Math.round(p.price / p.area) : null)} 
                  type="price"
                  lowerIsBetter={true}
                />
                <CompareRow 
                  label="Bedrooms" 
                  icon={Bed}
                  values={properties.map(p => p.bedrooms)} 
                />
                <CompareRow 
                  label="Bathrooms" 
                  icon={Bath}
                  values={properties.map(p => p.bathrooms)} 
                />
                <CompareRow 
                  label="Parking" 
                  icon={Car}
                  values={properties.map(p => p.parking)} 
                />
                <CompareRow 
                  label="Floor" 
                  icon={Layers}
                  values={properties.map(p => p.floor ? `${p.floor} of ${p.total_floors}` : null)} 
                />
                <CompareRow 
                  label="Furnishing" 
                  values={properties.map(p => p.furnishing)} 
                />
                <CompareRow 
                  label="Facing" 
                  values={properties.map(p => p.facing)} 
                />
                <CompareRow 
                  label="Age" 
                  values={properties.map(p => p.age)} 
                />
                <CompareRow 
                  label="Location" 
                  icon={MapPin}
                  values={properties.map(p => `${p.city}, ${p.state}`)} 
                />
                
                {/* Amenities Row */}
                <tr className="border-b">
                  <td className="p-4 font-medium text-[#0F172A] bg-[#F9F9F7] align-top">Amenities</td>
                  {properties.map((prop) => (
                    <td key={prop.property_id} className="p-4 align-top">
                      {prop.amenities?.length > 0 ? (
                        <ul className="text-sm space-y-1">
                          {prop.amenities.slice(0, 6).map((amenity, i) => (
                            <li key={i} className="flex items-center gap-1">
                              <Check className="h-3 w-3 text-green-500" /> {amenity}
                            </li>
                          ))}
                          {prop.amenities.length > 6 && (
                            <li className="text-muted-foreground">+{prop.amenities.length - 6} more</li>
                          )}
                        </ul>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                  ))}
                  {[...Array(4 - properties.length)].map((_, i) => (
                    <td key={`empty-amenities-${i}`} className="p-4 text-center text-muted-foreground">-</td>
                  ))}
                </tr>

                {/* View Property Link */}
                <tr>
                  <td className="p-4 bg-[#F9F9F7]"></td>
                  {properties.map((prop) => (
                    <td key={prop.property_id} className="p-4 text-center">
                      <Link to={`/properties/${prop.property_id}`}>
                        <Button variant="outline" size="sm" data-testid={`view-property-${prop.property_id}`}>
                          View Details
                        </Button>
                      </Link>
                    </td>
                  ))}
                  {[...Array(4 - properties.length)].map((_, i) => (
                    <td key={`empty-action-${i}`} className="p-4"></td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-sm border border-border/50">
            <div className="max-w-md mx-auto">
              <h3 className="text-xl font-medium text-[#0F172A] mb-2">Select Properties to Compare</h3>
              <p className="text-muted-foreground mb-6">
                Add at least 2 properties to start comparing. Click the "Add Property" button above.
              </p>
              <Link to="/properties">
                <Button className="btn-primary" data-testid="browse-properties-btn">Browse Properties</Button>
              </Link>
            </div>
          </div>
        )}

        {/* Property Selector Modal */}
        {showSelector && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-sm max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-medium text-[#0F172A]">Select a Property</h3>
                <button onClick={() => setShowSelector(false)} data-testid="close-selector">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {allProperties.filter(p => !selectedIds.includes(p.property_id)).length > 0 ? (
                  <div className="space-y-2">
                    {allProperties
                      .filter(p => !selectedIds.includes(p.property_id))
                      .map((prop) => (
                        <div
                          key={prop.property_id}
                          onClick={() => addProperty(prop.property_id)}
                          className="flex items-center gap-4 p-3 rounded-sm hover:bg-[#F9F9F7] cursor-pointer transition-colors"
                          data-testid={`select-property-${prop.property_id}`}
                        >
                          <img
                            src={prop.images?.[0] || defaultImage}
                            alt={prop.title}
                            className="h-16 w-16 rounded-sm object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-[#0F172A] truncate">{prop.title}</h4>
                            <p className="text-sm text-muted-foreground">{prop.city} • {formatPrice(prop.price)}</p>
                          </div>
                          <Plus className="h-5 w-5 text-muted-foreground" />
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No more properties available to compare
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparePage;
